import React, { useMemo } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { BellIcon } from './Icons';
import { Station, TimestampEntry } from '../types';

interface FeedItem {
  id: string;
  type: 'news' | 'milestone';
  text: string;
  timestamp: number;
  sender?: string;
  milestoneType?: string;
}

const NewsBar: React.FC = () => {
  const { newsFeed, selectedFlightData } = useFlight();

  const combinedFeed = useMemo(() => {
    const feed: FeedItem[] = [];

    // Add news messages
    newsFeed.forEach(msg => {
      feed.push({
        ...msg,
        type: 'news',
      });
    });

    const crewMovement = selectedFlightData?.crewMovement;

    const getLatestTimestamp = (station: Station, types: ('crew' | 'pilots')[]): TimestampEntry | null => {
      if (!crewMovement || !crewMovement[station]) return null;
      let latest: TimestampEntry | null = null;
      
      types.forEach(type => {
        const entries = crewMovement[station]?.[type];
        if (entries && entries.length > 0) {
          const lastEntry = entries[entries.length - 1];
          if (!latest || new Date(lastEntry.time).getTime() > new Date(latest.time).getTime()) {
            latest = lastEntry;
          }
        }
      });
      return latest;
    };
    
    const milestones: { name: Station; label: string; milestoneType: string; types: ('crew' | 'pilots')[] }[] = [
        { name: 'Crew Arrived', label: 'Crew Arrived', milestoneType: 'crew_arrived', types: ['crew', 'pilots'] },
        { name: '1st Pax IN', label: 'Boarding Started', milestoneType: 'boarding_started', types: ['crew'] },
        { name: 'Doors Closed', label: 'Doors Closed', milestoneType: 'doors_closed', types: ['crew'] }
    ];

    milestones.forEach(milestone => {
        const timestampEntry = getLatestTimestamp(milestone.name, milestone.types);
        if (timestampEntry) {
            feed.push({
                id: `milestone-${milestone.milestoneType}`,
                type: 'milestone',
                milestoneType: milestone.milestoneType,
                text: `${milestone.label} at ${new Date(timestampEntry.time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
                timestamp: new Date(timestampEntry.time).getTime(),
            });
        }
    });

    return feed.sort((a, b) => a.timestamp - b.timestamp);

  }, [newsFeed, selectedFlightData?.crewMovement]);


  if (!combinedFeed || combinedFeed.length === 0) {
    return null;
  }
  
  const fullTextForDuration = combinedFeed.map(item => {
    if (item.type === 'news') {
        return `[${item.sender}]: ${item.text}`;
    }
    return item.text;
  }).join('  •  ');
    
  const duration = Math.max(20, fullTextForDuration.length / 5);
  
  const getMilestoneColorClass = (milestoneType?: string): string => {
    switch (milestoneType) {
      case 'crew_arrived':
        return 'text-amber-300 font-bold';
      case 'boarding_started':
        return 'text-green-300 font-bold';
      case 'doors_closed':
        return 'text-cyan-300 font-bold';
      default:
        return 'text-white';
    }
  };

  return (
    <div data-tour-id="news-bar" className="bg-red-700 border-b-2 border-red-800 text-white flex items-center py-1.5 px-4 text-sm font-medium overflow-hidden no-print">
      <BellIcon className="w-5 h-5 mr-3 flex-shrink-0 animate-pulse"/>
      <div className="whitespace-nowrap overflow-hidden flex-grow">
        <div className="inline-block animate-marquee" style={{ animationDuration: `${duration}s` }}>
          {combinedFeed.map((item, index) => (
            <React.Fragment key={item.id}>
              <span className={getMilestoneColorClass(item.milestoneType)}>
                {item.type === 'news' ? `[${item.sender}]: ${item.text}` : item.text}
              </span>
              {index < combinedFeed.length - 1 && <span className="text-red-300 mx-3">•</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsBar;